import { animate } from 'animejs';

let isAnimating = false;

function notifyShanshui(type: 'shanshui-pause' | 'shanshui-resume'): void {
  const iframe = document.getElementById('shanshui-bg') as HTMLIFrameElement | null;
  if (!iframe) return;

  // Direct synchronous execution across same-origin iframe boundary for 0ms pause latency
  try {
    const win = iframe.contentWindow as (Window & { pauseShanshui?: () => void; resumeShanshui?: () => void }) | null;
    if (type === 'shanshui-pause' && win?.pauseShanshui) {
      win.pauseShanshui();
    } else if (type === 'shanshui-resume' && win?.resumeShanshui) {
      win.resumeShanshui();
    }
  } catch {
    // Cross-origin fallback
  }

  // Also send postMessage as fallback
  iframe.contentWindow?.postMessage({ type }, '*');
}

export async function openScrollModal(modalEl: HTMLElement): Promise<void> {
  if (isAnimating) return;
  isAnimating = true;

  // Instantly pause background procedural canvas to free CPU and GPU
  notifyShanshui('shanshui-pause');

  modalEl.classList.add('is-open');
  modalEl.setAttribute('aria-hidden', 'false');

  const backdrop = modalEl.querySelector<HTMLElement>('.scroll-modal-backdrop');
  const windowEl = modalEl.querySelector<HTMLElement>('.scroll-window');
  const content = modalEl.querySelector<HTMLElement>('.scroll-inner-paper');

  if (!backdrop || !windowEl || !content) {
    isAnimating = false;
    return;
  }

  try {
    // Calculate target parchment height to fit viewport cleanly without modal clipping
    const availableHeight = window.innerHeight - 150;
    const targetHeight = Math.max(280, Math.min(availableHeight, 660));

    // Reset states: content is kept display: none so zero layout computations occur during unfurling
    backdrop.style.opacity = '0';
    windowEl.style.height = '0px';
    content.style.display = 'none';
    content.style.opacity = '0';
    content.style.transform = 'translateY(-12px)';

    // Animate backdrop fade in concurrently
    animate(backdrop, {
      opacity: [0, 1],
      duration: 250,
      ease: 'outQuad',
    });

    // Unfurl the hanging scroll parchment smoothly
    await animate(windowEl, {
      height: [0, targetHeight],
      duration: 420,
      ease: 'outCubic',
    });

    windowEl.style.height = `${targetHeight}px`;

    // Reveal content smoothly once parchment is unfurled
    content.style.display = 'block';

    await animate(content, {
      opacity: [0, 1],
      translateY: [-12, 0],
      duration: 240,
      ease: 'outQuad',
    });
  } finally {
    isAnimating = false;
  }
}

export async function closeScrollModal(modalEl: HTMLElement): Promise<void> {
  if (isAnimating) return;
  isAnimating = true;

  const backdrop = modalEl.querySelector<HTMLElement>('.scroll-modal-backdrop');
  const windowEl = modalEl.querySelector<HTMLElement>('.scroll-window');
  const content = modalEl.querySelector<HTMLElement>('.scroll-inner-paper');

  if (!backdrop || !windowEl || !content) {
    isAnimating = false;
    return;
  }

  try {
    const currentHeight = windowEl.offsetHeight;

    // Content rolls up quickly
    await animate(content, {
      opacity: [1, 0],
      translateY: [0, -10],
      duration: 140,
      ease: 'inQuad',
    });

    // Hide inner paper completely to eliminate all layout calculations during collapse
    content.style.display = 'none';

    // Concurrently fade out backdrop
    animate(backdrop, {
      opacity: [1, 0],
      duration: 260,
      ease: 'inQuad',
    });

    // Roll up bottom roller & collapse parchment
    await animate(windowEl, {
      height: [currentHeight, 0],
      duration: 340,
      ease: 'inCubic',
    });

    modalEl.classList.remove('is-open');
    modalEl.setAttribute('aria-hidden', 'true');
  } finally {
    isAnimating = false;
    // Resume background animation
    notifyShanshui('shanshui-resume');
  }
}